/**
=========================================================
* Material Dashboard 3 PRO React - v2.4.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useMemo, useRef, useEffect } from "react";
import PropTypes from "prop-types";
import swal from "sweetalert";

// Material Dashboard 3 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDDropzone from "components/MDDropzone";

// Validasi tipe file
const isValidFileType = (file) => {
  const allowedTypes = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-excel.sheet.macroEnabled.12",
    "image/jpeg",
    "image/jpg",
    "image/png",
  ];
  const allowedExtensions = [
    ".pdf",
    ".doc",
    ".docx",
    ".xls",
    ".xlsx",
    ".xlsm",
    ".jpg",
    ".jpeg",
    ".png",
  ];
  const fileExtension = file.name
    .toLowerCase()
    .substring(file.name.lastIndexOf("."));
  return (
    allowedTypes.includes(file.type) ||
    allowedExtensions.includes(fileExtension)
  );
};

// Validasi ukuran file individual (tidak ada batas per file, hanya total)
const isValidFileSize = (file) => {
  // Tidak ada batas per file, hanya total yang dibatasi
  return true;
};

// Validasi total ukuran semua file (1.5 MB = 1.5 * 1024 * 1024 bytes)
const isValidTotalSize = (files) => {
  const maxTotalSize = 1.5 * 1024 * 1024; // 1.5 MB total dalam bytes
  const totalSize = files.reduce((sum, file) => {
    const fileObj = file.file || file;
    return sum + (fileObj.size || 0);
  }, 0);
  return totalSize <= maxTotalSize;
};

// Validasi file lengkap (tipe dan ukuran total)
const isValidFile = (file, existingFilesSize = 0) => {
  // Validasi tipe file
  if (!isValidFileType(file)) {
    return { valid: false, reason: "Format file tidak didukung" };
  }

  // Validasi ukuran total (termasuk file baru ini)
  const newTotalSize = existingFilesSize + (file.size || 0);
  const maxTotalSize = 1.5 * 1024 * 1024; // 1.5 MB

  if (newTotalSize > maxTotalSize) {
    const totalSizeMB = newTotalSize / (1024 * 1024);
    return {
      valid: false,
      reason: `Total ukuran melebihi 1.5 MB (${totalSizeMB.toFixed(2)} MB)`,
    };
  }

  return { valid: true };
};

// Fungsi untuk memvalidasi semua file (tipe dan ukuran)
const validateAllFilesMedia = (files) => {
  if (!files || !Array.isArray(files) || files.length === 0) {
    return { isValid: true, errors: [] };
  }

  const maxTotalSize = 1.5 * 1024 * 1024; // 1.5 MB dalam bytes
  const errors = [];
  let totalSize = 0;

  files.forEach((fileItem, index) => {
    const fileObj =
      fileItem && typeof fileItem === "object"
        ? fileItem.file || fileItem
        : fileItem;

    // Validasi bahwa fileObj adalah object
    if (!fileObj || typeof fileObj !== "object") {
      errors.push({
        name: `File ${index + 1}`,
        reason: "File tidak valid (bukan object file)",
      });
      return;
    }

    // Validasi bahwa file memiliki nama
    if (!fileObj.name || typeof fileObj.name !== "string") {
      errors.push({
        name: `File ${index + 1}`,
        reason: "File tidak memiliki nama yang valid",
      });
      return;
    }

    // Validasi ukuran file
    const fileSize = fileObj.size;
    if (!fileSize || typeof fileSize !== "number" || fileSize <= 0) {
      errors.push({
        name: fileObj.name,
        reason: "File tidak memiliki ukuran yang valid",
      });
      return;
    }

    // Validasi tipe file
    if (!isValidFileType(fileObj)) {
      errors.push({
        name: fileObj.name,
        reason: "Format file tidak didukung",
      });
      return;
    }

    // Cek apakah penambahan file ini akan melebihi batas ukuran total
    const newTotalSize = totalSize + fileSize;
    if (newTotalSize > maxTotalSize) {
      const totalSizeMB = newTotalSize / (1024 * 1024);
      errors.push({
        name: fileObj.name,
        reason: `Total ukuran akan melebihi 1.5 MB (${totalSizeMB.toFixed(
          2
        )} MB)`,
      });
      return;
    }

    // File valid - tambahkan ke total ukuran
    totalSize += fileSize;
  });

  return {
    isValid: errors.length === 0,
    errors: errors,
    totalSize: totalSize,
  };
};

function Media({ values, onChange, onValidationChange }) {
  const dropzoneRef = useRef(null);

  // Inject file ke Dropzone saat mount jika dokumen tidak null
  useEffect(() => {
    if (
      dropzoneRef.current &&
      values.dokumen &&
      Array.isArray(values.dokumen) &&
      values.dokumen.length > 0
    ) {
      const dz = dropzoneRef.current.dropzone;
      // Hanya inject jika Dropzone kosong
      if (dz && dz.files.length === 0) {
        values.dokumen.forEach((file) => {
          const fileObj = file.file || file;
          // Validasi file sebelum inject (hanya tipe, ukuran dicek di handler accept)
          const validation = isValidFile(fileObj, 0);
          if (!validation.valid) {
            console.warn(
              `File ${fileObj.name} tidak valid dan akan diabaikan: ${validation.reason}`
            );
            return;
          }
          // Hindari duplikasi file
          if (
            !dz.files.find(
              (f) => f.name === fileObj.name && f.size === fileObj.size
            )
          ) {
            dz.emit("addedfile", fileObj);
            dz.emit("complete", fileObj);
          }
        });
      }
    }
  }, [values.dokumen]);

  // Event handler untuk memvalidasi dan menolak file sebelum ditambahkan ke dropzone
  useEffect(() => {
    const dz = dropzoneRef.current?.dropzone;
    if (!dz) return;

    // Handler untuk memvalidasi file sebelum ditambahkan (menggunakan accept)
    const handleAccept = (file, done) => {
      // Hitung total ukuran file yang sudah ada di dropzone
      const existingFilesSize = dz.files.reduce((sum, f) => {
        return sum + (f.size || 0);
      }, 0);

      // Validasi file lengkap (tipe dan ukuran)
      const validation = isValidFile(file, existingFilesSize);

      if (!validation.valid) {
        // File tidak valid (tipe tidak didukung atau ukuran melebihi batas)
        done(validation.reason);

        // Tampilkan pesan error
        if (validation.reason.includes("Format file")) {
          swal({
            title: "Format File Tidak Didukung",
            text: `File "${file.name}" tidak dapat diupload.\n\n${validation.reason}\n\nFormat yang didukung: PDF, Word (DOC/DOCX), Excel (XLS/XLSX), JPG, PNG`,
            icon: "error",
            button: "OK",
          });
        } else if (validation.reason.includes("ukuran")) {
          const totalSizeMB = (existingFilesSize + file.size) / (1024 * 1024);
          swal({
            title: "Ukuran File Melebihi Batas",
            text: `Total ukuran semua file (${totalSizeMB.toFixed(
              2
            )} MB) melebihi batas maksimal 1.5 MB.\n\nSilakan hapus beberapa file yang sudah ada atau pilih file yang lebih kecil.`,
            icon: "error",
            button: "OK",
          });
        }
        return;
      }

      // File diterima (tipe valid dan ukuran tidak melebihi batas)
      done(null);
    };

    // Handler untuk memvalidasi multiple files (menggunakan addedfiles sebagai fallback)
    const handleAddedFiles = (files) => {
      // Gunakan setTimeout untuk memastikan file sudah ditambahkan ke dz.files
      setTimeout(() => {
        // Hitung total ukuran file yang sekarang ada di dropzone
        const allFiles = dz.files;
        const totalSize = allFiles.reduce((sum, file) => {
          return sum + (file.size || 0);
        }, 0);
        const maxTotalSize = 1.5 * 1024 * 1024; // 1.5 MB
        const totalSizeMB = totalSize / (1024 * 1024);

        // Jika total ukuran melebihi batas, hapus file yang baru ditambahkan
        if (totalSize > maxTotalSize) {
          // Hapus file yang baru ditambahkan (file yang ada di parameter files)
          files.forEach((file) => {
            // Validasi tipe file terlebih dahulu
            if (!isValidFileType(file)) {
              // File dengan tipe tidak valid sudah ditolak oleh dropzone sendiri
              return;
            }
            // Hapus file yang menyebabkan total melebihi batas
            if (dz.files.includes(file)) {
              dz.emit("error", file, `Total ukuran melebihi 1.5 MB`);
              dz.removeFile(file);
            }
          });

          // Tampilkan pesan error
          swal({
            title: "Ukuran File Melebihi Batas",
            text: `Total ukuran semua file (${totalSizeMB.toFixed(
              2
            )} MB) melebihi batas maksimal 1.5 MB.\n\nSilakan hapus beberapa file yang sudah ada atau pilih file yang lebih kecil.`,
            icon: "error",
            button: "OK",
          });
        }
      }, 0);
    };

    // Tambahkan event listener untuk accept (validasi sebelum file ditambahkan)
    dz.on("accept", handleAccept);

    // Tambahkan event listener untuk addedfiles sebagai fallback
    dz.on("addedfiles", handleAddedFiles);

    // Cleanup: hapus event listener saat unmount
    return () => {
      if (dz) {
        dz.off("accept", handleAccept);
        dz.off("addedfiles", handleAddedFiles);
      }
    };
  }, []);

  // Handler: update dokumen di parent sesuai file di Dropzone
  // File yang sudah ditolak di event handler tidak akan sampai ke sini
  const handleDropzoneChange = (files) => {
    // Filter file yang valid berdasarkan tipe dan ukuran (sebagai double check)
    const validFiles = [];
    const invalidFiles = [];

    // Hitung total ukuran untuk validasi
    let totalSize = 0;

    files.forEach((file) => {
      const fileObj = file.file || file;
      const validation = isValidFile(fileObj, totalSize);

      if (!validation.valid) {
        invalidFiles.push({
          name: fileObj.name,
          reason: validation.reason,
        });
      } else {
        validFiles.push(file);
        totalSize += fileObj.size || 0;
      }
    });

    // Tampilkan pesan error jika ada file yang tidak valid (seharusnya tidak terjadi karena sudah divalidasi di accept)
    if (invalidFiles.length > 0) {
      const errorMessages = invalidFiles
        .map((f) => `• ${f.name}: ${f.reason}`)
        .join("\n");
      swal({
        title: "File Tidak Valid",
        text: `File berikut tidak dapat diupload:\n\n${errorMessages}\n\nFormat yang didukung: PDF, Word (DOC/DOCX), Excel (XLS/XLSX), JPG, PNG`,
        icon: "error",
        button: "OK",
      });
    }

    // Update dengan file yang valid (file yang melebihi total ukuran atau tipe tidak valid sudah ditolak)
    // Hanya kirim ke sistem jika semua file valid
    if (typeof onChange === "function" && invalidFiles.length === 0) {
      onChange({ dokumen: validFiles });
    }

    // Kirim status validitas ke parent untuk menonaktifkan tombol send
    if (typeof onValidationChange === "function") {
      const validation = validateAllFilesMedia(files);
      // Nonaktifkan tombol send jika ada file yang tidak valid
      onValidationChange(!validation.isValid);
    }
  };

  // Validasi file saat mount atau saat values.dokumen berubah
  // Kirim status validitas ke parent untuk menonaktifkan tombol send
  useEffect(() => {
    if (typeof onValidationChange === "function") {
      const files = values.dokumen || [];
      const validation = validateAllFilesMedia(files);
      // Nonaktifkan tombol send jika ada file yang tidak valid
      // isInvalid = true berarti tombol send harus dinonaktifkan
      onValidationChange(!validation.isValid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.dokumen]); // onValidationChange tidak perlu di dependencies karena sudah stabil (useCallback)

  // Preview file yang sudah diupload (tabel)
  const renderPreview = () => {
    if (values.dokumen && values.dokumen.length > 0) {
      // Hitung total ukuran
      const totalSize = values.dokumen.reduce((sum, file) => {
        const fileObj = file.file || file;
        return sum + (fileObj.size || 0);
      }, 0);
      const totalSizeMB = totalSize / (1024 * 1024);
      const maxSizeMB = 1.5;
      const isExceeding = totalSizeMB > maxSizeMB;

      return (
        <div style={{ marginBottom: 16 }}>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <MDTypography variant="button" fontWeight="bold" color="text">
              File terupload:
            </MDTypography>
            <MDTypography
              variant="caption"
              color={isExceeding ? "error" : "text.secondary"}
              fontWeight="medium"
            >
              Total: {totalSizeMB.toFixed(2)} MB / {maxSizeMB} MB
            </MDTypography>
          </MDBox>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              marginTop: 8,
              fontSize: 13,
              fontFamily: "inherit, Arial, sans-serif",
            }}
          >
            <thead>
              <tr style={{ background: "#f5f5f5" }}>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: 8,
                    textAlign: "center",
                    fontWeight: 700,
                    fontSize: 13,
                    fontFamily: "inherit, Arial, sans-serif",
                  }}
                >
                  No
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: 8,
                    textAlign: "left",
                    fontWeight: 700,
                    fontSize: 13,
                    fontFamily: "inherit, Arial, sans-serif",
                  }}
                >
                  Nama File
                </th>
                <th
                  style={{
                    border: "1px solid #ddd",
                    padding: 8,
                    textAlign: "right",
                    fontWeight: 700,
                    fontSize: 13,
                    fontFamily: "inherit, Arial, sans-serif",
                  }}
                >
                  Ukuran
                </th>
              </tr>
            </thead>
            <tbody>
              {values.dokumen.map((file, idx) => {
                const fileObj = file.file || file;
                return (
                  <tr key={idx}>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: 8,
                        textAlign: "center",
                        fontWeight: 400,
                        fontSize: 12,
                        fontFamily: "inherit, Arial, sans-serif",
                      }}
                    >
                      {idx + 1}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: 8,
                        fontWeight: 400,
                        fontSize: 12,
                        fontFamily: "inherit, Arial, sans-serif",
                      }}
                    >
                      {fileObj.name || "Unknown"}
                    </td>
                    <td
                      style={{
                        border: "1px solid #ddd",
                        padding: 8,
                        textAlign: "right",
                        fontWeight: 400,
                        fontSize: 12,
                        fontFamily: "inherit, Arial, sans-serif",
                      }}
                    >
                      {fileObj.size
                        ? `${(fileObj.size / 1024).toFixed(2)} KB`
                        : "-"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      );
    }
    return null;
  };

  return (
    <MDBox>
      <MDTypography variant="h5">Dokumen</MDTypography>
      <MDBox mt={3}>
        <MDBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
          <MDTypography
            component="label"
            variant="button"
            fontWeight="regular"
            color="text"
          >
            Upload dokumen, jika ada (bisa lebih dari satu file)
            <br />
            <MDTypography
              component="span"
              variant="caption"
              color="text.secondary"
            >
              Format: PDF, Word, Excel, JPG, PNG | Maksimal total: 1.5 MB untuk
              semua file
            </MDTypography>
          </MDTypography>
        </MDBox>
        {renderPreview()}
        {useMemo(
          () => (
            <MDDropzone
              options={{
                addRemoveLinks: true,
                uploadMultiple: true,
                maxTotalFilesize: 1.5, // Maksimal 1.5 MB total untuk semua file
                acceptedFiles:
                  ".pdf,.doc,.docx,.xls,.xlsx,.xlsm,.jpg,.jpeg,.png", // PDF, Word, Excel, JPG, PNG
                dictDefaultMessage:
                  "Klik atau seret file ke sini untuk mengunggah",
                dictTotalFilesTooBig:
                  "Total ukuran semua file melebihi batas maksimal 1.5 MB",
                dictInvalidFileType:
                  "Format file tidak didukung. Hanya PDF, Word (DOC/DOCX), Excel (XLS/XLSX), JPG, dan PNG yang diperbolehkan",
                dictResponseError: "Terjadi kesalahan saat mengunggah file",
                dictCancelUpload: "Batalkan unggah",
                dictUploadCanceled: "Unggah dibatalkan",
                dictCancelUploadConfirmation:
                  "Apakah Anda yakin ingin membatalkan unggah?",
                dictRemoveFile: "Hapus file",
              }}
              onChange={handleDropzoneChange}
              ref={dropzoneRef}
            />
          ),
          []
        )}
        <MDBox mt={1}>
          <MDTypography variant="caption" color="text.secondary">
            * Format yang didukung: PDF, Word (DOC/DOCX), Excel (XLS/XLSX), JPG,
            PNG
            <br />* Maksimal total ukuran semua file: 1.5 MB
            <br />* File dengan format lain atau ukuran melebihi batas tidak
            dapat diupload
          </MDTypography>
        </MDBox>
      </MDBox>
    </MDBox>
  );
}

Media.propTypes = {
  values: PropTypes.object,
  onChange: PropTypes.func,
  onValidationChange: PropTypes.func, // Callback untuk mengirim status validitas file
};

export default Media;
